import { useEffect, useState, type ReactElement } from "react";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { BackgroundColor, Color, FontSize, TextStyle } from "@tiptap/extension-text-style";
import { TextAlign } from "@tiptap/extension-text-align";
import { BLOCOS, BlocoEmail } from "./blocoEmail";
import {
  Box,
  Button,
  Divider,
  Flex,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  SimpleGrid,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import {
  MdFormatAlignCenter,
  MdFormatAlignLeft,
  MdFormatAlignRight,
  MdFormatBold,
  MdFormatColorFill,
  MdFormatColorText,
  MdFormatItalic,
  MdFormatListBulleted,
  MdFormatListNumbered,
  MdFormatSize,
  MdFormatUnderlined,
  MdHorizontalRule,
  MdLink,
  MdTitle,
  MdViewDay,
} from "react-icons/md";
import { useVariavelInsert } from "./VariavelInsertContext";

type Props = {
  value: string;
  onChange: (html: string) => void;
  /** Canal dono do texto — o mapa de variáveis é por canal. */
  canalId: string;
  isDisabled?: boolean;
};

// Paletas tiradas dos e-mails de produção (templates do consignado-trabalhador),
// para o que for montado aqui sair com as mesmas cores.
const CORES_TEXTO: { cor: string; nome: string }[] = [
  { cor: "#333333", nome: "Texto" },
  { cor: "#666666", nome: "Cinza" },
  { cor: "#5b9b7f", nome: "Verde GranaTech" },
  { cor: "#7bc77f", nome: "Verde claro" },
  { cor: "#4a7c9e", nome: "Azul" },
  { cor: "#dc3545", nome: "Vermelho" },
  { cor: "#721c24", nome: "Vinho" },
  { cor: "#ffc107", nome: "Amarelo" },
];

const CORES_FUNDO: { cor: string; nome: string }[] = [
  { cor: "#fff3cd", nome: "Atenção" },
  { cor: "#e8f4f0", nome: "Verde suave" },
  { cor: "#f8fcff", nome: "Azul suave" },
  { cor: "#fff5f5", nome: "Vermelho suave" },
  { cor: "#f5f5f5", nome: "Cinza suave" },
  { cor: "#5b9b7f", nome: "Verde GranaTech" },
];

const TAMANHOS: { valor: string | null; nome: string }[] = [
  { valor: "12px", nome: "Pequeno" },
  { valor: null, nome: "Normal" },
  { valor: "18px", nome: "Grande" },
  { valor: "24px", nome: "Muito grande" },
];

// Editor de corpo próprio com formatação. Só aparece pro e-mail em modo
// HTML: o backend recusa `tipoConteudo: "HTML"` em qualquer canal que não
// seja e-mail, então SMS e WhatsApp usam o textarea simples. Cores, tamanho e
// alinhamento saem como estilo inline (`<span style>`, `<p style>`), que é o
// que os clientes de e-mail respeitam.
//
// A lista de variáveis não fica aqui: ao focar, o editor se registra como
// "alvo ativo" na sidebar da tela inteira (ver VariavelInsertContext).
export default function RichTextEditor({ value, onChange, canalId, isDisabled }: Props) {
  const { registerActiveInsert } = useVariavelInsert();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: { openOnClick: false, defaultProtocol: "https" } }),
      TextStyle,
      Color,
      BackgroundColor,
      FontSize,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      BlocoEmail,
    ],
    content: value,
    editable: !isDisabled,
    immediatelyRender: false,
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
    onFocus: ({ editor: e }) => {
      registerActiveInsert(canalId, (token) => e.chain().focus().insertContent(`${token} `).run());
    },
  });

  // Registra assim que o editor existe, não só ao focar — a sidebar deve
  // oferecer variáveis assim que o canal é aberto, sem exigir um clique no
  // campo primeiro.
  useEffect(() => {
    if (!editor) return;
    registerActiveInsert(canalId, (token) => editor.chain().focus().insertContent(`${token} `).run());
  }, [editor, canalId, registerActiveInsert]);

  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  useEffect(() => {
    editor?.setEditable(!isDisabled);
  }, [isDisabled, editor]);

  if (!editor) return null;

  const ativo = !isDisabled;

  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
      <Flex borderBottomWidth="1px" borderColor="gray.200" bg="gray.50" p={1} gap={1} align="center" wrap="wrap">
        <BotaoFormato editor={editor} ativo={ativo} rotulo="Negrito" icone={<MdFormatBold />} marca="bold"
          acao={(c) => c.toggleBold()} />
        <BotaoFormato editor={editor} ativo={ativo} rotulo="Itálico" icone={<MdFormatItalic />} marca="italic"
          acao={(c) => c.toggleItalic()} />
        <BotaoFormato editor={editor} ativo={ativo} rotulo="Sublinhado" icone={<MdFormatUnderlined />} marca="underline"
          acao={(c) => c.toggleUnderline()} />
        <BotaoFormato editor={editor} ativo={ativo} rotulo="Título" icone={<MdTitle />} marca="heading" atributos={{ level: 2 }}
          acao={(c) => c.toggleHeading({ level: 2 })} />
        <BotaoFormato editor={editor} ativo={ativo} rotulo="Lista com marcadores" icone={<MdFormatListBulleted />} marca="bulletList"
          acao={(c) => c.toggleBulletList()} />
        <BotaoFormato editor={editor} ativo={ativo} rotulo="Lista numerada" icone={<MdFormatListNumbered />} marca="orderedList"
          acao={(c) => c.toggleOrderedList()} />
        <BotaoLink editor={editor} ativo={ativo} />
        <BotaoFormato editor={editor} ativo={ativo} rotulo="Linha divisória" icone={<MdHorizontalRule />}
          acao={(c) => c.setHorizontalRule()} />

        <Divider orientation="vertical" h="20px" mx={1} />
        <BotaoCor
          editor={editor}
          ativo={ativo}
          rotulo="Cor do texto"
          icone={<MdFormatColorText />}
          cores={CORES_TEXTO}
          atual={editor.getAttributes("textStyle").color as string | undefined}
          aplicar={(cor) => editor.chain().focus().setColor(cor).run()}
          limpar={() => editor.chain().focus().unsetColor().run()}
        />
        <BotaoCor
          editor={editor}
          ativo={ativo}
          rotulo="Cor de fundo"
          icone={<MdFormatColorFill />}
          cores={CORES_FUNDO}
          atual={editor.getAttributes("textStyle").backgroundColor as string | undefined}
          aplicar={(cor) => editor.chain().focus().setBackgroundColor(cor).run()}
          limpar={() => editor.chain().focus().unsetBackgroundColor().run()}
        />
        <BotaoTamanho editor={editor} ativo={ativo} />

        <Divider orientation="vertical" h="20px" mx={1} />
        <BotaoFormato editor={editor} ativo={ativo} rotulo="Alinhar à esquerda" icone={<MdFormatAlignLeft />}
          ligadoQuando={() => editor.isActive({ textAlign: "left" })}
          acao={(c) => c.setTextAlign("left")} />
        <BotaoFormato editor={editor} ativo={ativo} rotulo="Centralizar" icone={<MdFormatAlignCenter />}
          ligadoQuando={() => editor.isActive({ textAlign: "center" })}
          acao={(c) => c.setTextAlign("center")} />
        <BotaoFormato editor={editor} ativo={ativo} rotulo="Alinhar à direita" icone={<MdFormatAlignRight />}
          ligadoQuando={() => editor.isActive({ textAlign: "right" })}
          acao={(c) => c.setTextAlign("right")} />

        <Divider orientation="vertical" h="20px" mx={1} />
        <BotaoBloco editor={editor} ativo={ativo} />
      </Flex>
      {/* A área de edição imita o e-mail que chega: a moldura de produção
          (coluna branca de 650px com borda sobre fundo cinza, a mesma que entra
          ao salvar — ver blocoEmail) e o padrão dos clientes de e-mail para
          fonte, parágrafos, títulos e listas. */}
      <Box bg="#f5f5f5" px={{ base: 2, md: 4 }} py={4}>
      <Box
        maxW="650px"
        mx="auto"
        bg="white"
        borderWidth="1px"
        borderColor="#e0e0e0"
        px="32px"
        minH="160px"
        sx={{
          ".ProseMirror": {
            outline: "none",
            minHeight: "140px",
            paddingY: "1px",
            fontFamily: "Arial, Helvetica, sans-serif",
            fontSize: "14px",
            lineHeight: 1.6,
            color: "#333333",
          },
          ".ProseMirror p": { margin: "1em 0" },
          ".ProseMirror li p": { margin: 0 },
          ".ProseMirror ul": { listStyleType: "disc", margin: "1em 0", paddingLeft: "40px" },
          ".ProseMirror ol": { listStyleType: "decimal", margin: "1em 0", paddingLeft: "40px" },
          ".ProseMirror h1": { fontSize: "2em", fontWeight: "bold", margin: "0.67em 0" },
          ".ProseMirror h2": { fontSize: "1.5em", fontWeight: "bold", margin: "0.83em 0" },
          ".ProseMirror hr": { margin: "0.5em 0", border: 0, borderTop: "1px solid #c8c8c8" },
          ".ProseMirror a": { color: "#1155cc", textDecoration: "underline" },
          ".ProseMirror [data-bloco]": { outline: "1px dashed transparent" },
          ".ProseMirror [data-bloco]:hover": { outlineColor: "#a0aec0" },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
      </Box>
    </Box>
  );
}

type Cadeia = ReturnType<Editor["chain"]>;

function BotaoFormato({
  editor,
  ativo,
  rotulo,
  icone,
  marca,
  atributos,
  ligadoQuando,
  acao,
}: {
  editor: Editor;
  ativo: boolean;
  rotulo: string;
  icone: ReactElement;
  /** Marca ou nó que acende o botão quando o cursor está nele. */
  marca?: string;
  atributos?: Record<string, unknown>;
  /** Para o que não é marca nem nó (ex.: alinhamento). */
  ligadoQuando?: () => boolean;
  acao: (cadeia: Cadeia) => Cadeia;
}) {
  const ligado = ligadoQuando ? ligadoQuando() : !!marca && editor.isActive(marca, atributos);
  return (
    <Tooltip label={rotulo}>
      <IconButton
        aria-label={rotulo}
        icon={icone}
        size="sm"
        variant={ligado ? "solid" : "ghost"}
        colorScheme={ligado ? "green" : "gray"}
        isDisabled={!ativo}
        onClick={() => acao(editor.chain().focus()).run()}
      />
    </Tooltip>
  );
}

// Sem protocolo ("granatech.com.br"), o cliente de e-mail trataria o endereço
// como caminho relativo e o link não abriria.
const comProtocolo = (url: string): string =>
  /^(https?:|mailto:|tel:|#|\{\{)/i.test(url) ? url : `https://${url}`;

// Link no trecho selecionado. Endereço vazio remove o link.
function BotaoLink({ editor, ativo }: { editor: Editor; ativo: boolean }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [url, setUrl] = useState("");
  const ligado = editor.isActive("link");

  const abrir = () => {
    setUrl((editor.getAttributes("link").href as string | undefined) ?? "");
    onOpen();
  };
  const aplicar = () => {
    const destino = url.trim();
    const cadeia = editor.chain().focus().extendMarkRange("link");
    (destino ? cadeia.setLink({ href: comProtocolo(destino) }) : cadeia.unsetLink()).run();
    onClose();
  };
  const remover = () => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    onClose();
  };

  return (
    <Popover isOpen={isOpen} onOpen={abrir} onClose={onClose} placement="bottom-start" isLazy>
      <PopoverTrigger>
        <Box display="inline-flex">
          <Tooltip label="Link">
            <IconButton
              aria-label="Link"
              icon={<MdLink />}
              size="sm"
              variant={ligado ? "solid" : "ghost"}
              colorScheme={ligado ? "green" : "gray"}
              isDisabled={!ativo}
            />
          </Tooltip>
        </Box>
      </PopoverTrigger>
      <PopoverContent w="320px">
        <PopoverBody>
          <Input
            size="sm"
            autoFocus
            placeholder="https://"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && aplicar()}
            fontFamily="Lexend Deca"
          />
          <Divider my={2} />
          <Flex justify="flex-end" gap={2}>
            {ligado && (
              <Button size="xs" variant="ghost" onClick={remover} fontFamily="Lexend Deca">
                Remover link
              </Button>
            )}
            <Button size="xs" colorScheme="green" onClick={aplicar} fontFamily="Lexend Deca">
              Aplicar
            </Button>
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

// Cor do texto ou de fundo: paleta dos e-mails de produção, mais uma cor
// livre e "Sem cor" para tirar.
function BotaoCor({
  ativo,
  rotulo,
  icone,
  cores,
  atual,
  aplicar,
  limpar,
}: {
  editor: Editor;
  ativo: boolean;
  rotulo: string;
  icone: ReactElement;
  cores: { cor: string; nome: string }[];
  atual?: string;
  aplicar: (cor: string) => void;
  limpar: () => void;
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const escolher = (cor: string) => {
    aplicar(cor);
    onClose();
  };

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-start" isLazy>
      <PopoverTrigger>
        <Box display="inline-flex">
          <Tooltip label={rotulo}>
            <IconButton
              aria-label={rotulo}
              icon={icone}
              size="sm"
              variant="ghost"
              color={atual ?? undefined}
              borderBottomWidth="3px"
              borderBottomColor={atual ?? "transparent"}
              isDisabled={!ativo}
            />
          </Tooltip>
        </Box>
      </PopoverTrigger>
      <PopoverContent w="220px">
        <PopoverBody>
          <Text fontSize="xs" color="gray.600" mb={2} fontFamily="Lexend Deca">
            {rotulo}
          </Text>
          <SimpleGrid columns={4} spacing={2}>
            {cores.map((c) => (
              <Tooltip key={c.cor} label={c.nome}>
                <Box
                  as="button"
                  type="button"
                  aria-label={c.nome}
                  h="28px"
                  borderRadius="md"
                  bg={c.cor}
                  borderWidth={atual === c.cor ? "2px" : "1px"}
                  borderColor={atual === c.cor ? "gray.800" : "gray.300"}
                  onClick={() => escolher(c.cor)}
                />
              </Tooltip>
            ))}
          </SimpleGrid>
          <Divider my={2} />
          <Flex align="center" justify="space-between" gap={2}>
            <Flex align="center" gap={2}>
              <Input
                type="color"
                aria-label="Outra cor"
                size="xs"
                w="36px"
                p={0}
                border="none"
                value={atual?.startsWith("#") && atual.length === 7 ? atual : "#333333"}
                onChange={(e) => aplicar(e.target.value)}
              />
              <Text fontSize="xs" color="gray.600" fontFamily="Lexend Deca">
                Outra cor
              </Text>
            </Flex>
            <Button
              size="xs"
              variant="ghost"
              onClick={() => {
                limpar();
                onClose();
              }}
              fontFamily="Lexend Deca"
            >
              Sem cor
            </Button>
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

function BotaoTamanho({ editor, ativo }: { editor: Editor; ativo: boolean }) {
  const atual = (editor.getAttributes("textStyle").fontSize as string | undefined) ?? null;
  return (
    <Menu isLazy placement="bottom-start">
      <Tooltip label="Tamanho do texto">
        <MenuButton
          as={IconButton}
          aria-label="Tamanho do texto"
          icon={<MdFormatSize />}
          size="sm"
          variant={atual ? "solid" : "ghost"}
          colorScheme={atual ? "green" : "gray"}
          isDisabled={!ativo}
        />
      </Tooltip>
      <MenuList minW="160px" fontFamily="Lexend Deca" fontSize="sm">
        {TAMANHOS.map((t) => (
          <MenuItem
            key={t.nome}
            fontWeight={atual === t.valor ? "600" : "normal"}
            fontSize={t.valor ?? undefined}
            onClick={() => {
              const cadeia = editor.chain().focus();
              (t.valor ? cadeia.setFontSize(t.valor) : cadeia.unsetFontSize()).run();
            }}
          >
            {t.nome}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
}

// Envolve o trecho selecionado num bloco de layout (faixa ou caixa) com o
// estilo dos e-mails de produção. Dentro de um bloco, trocar o tipo troca o
// estilo; "Tirar do bloco" devolve o trecho ao corpo.
function BotaoBloco({ editor, ativo }: { editor: Editor; ativo: boolean }) {
  const atual = editor.isActive("blocoEmail")
    ? (editor.getAttributes("blocoEmail").estilo as string | undefined)
    : undefined;
  return (
    <Menu isLazy placement="bottom-start">
      <Tooltip label="Bloco: faixa ou caixa do layout do e-mail">
        <MenuButton
          as={Button}
          size="sm"
          leftIcon={<MdViewDay />}
          variant={atual ? "solid" : "ghost"}
          colorScheme={atual ? "green" : "gray"}
          isDisabled={!ativo}
          fontFamily="Lexend Deca"
          fontWeight="normal"
        >
          Bloco
        </MenuButton>
      </Tooltip>
      <MenuList minW="220px" fontFamily="Lexend Deca" fontSize="sm">
        {BLOCOS.map((b) => (
          <MenuItem
            key={b.id}
            fontWeight={atual === b.id ? "600" : "normal"}
            onClick={() => editor.chain().focus().setBlocoEmail(b.id).run()}
          >
            {b.nome}
          </MenuItem>
        ))}
        {atual && (
          <>
            <MenuDivider />
            <MenuItem onClick={() => editor.chain().focus().unsetBlocoEmail().run()}>Tirar do bloco</MenuItem>
          </>
        )}
      </MenuList>
    </Menu>
  );
}
